/**
 * Maze Solver Game
 * Navigate through mazes to reach the goal
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const MazeSolver = ({ onHome }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [score, setScore] = useLocalStorage('mazesolver_score', 0);
  const [highScore, setHighScore] = useLocalStorage('mazesolver_highScore', 0);
  const [level, setLevel] = useState(1);
  const [time, setTime] = useState(0);

  const gameStateRef = useRef({
    player: { x: 20, y: 20, width: 10, height: 10 },
    goal: { x: 380, y: 380, width: 20, height: 20 },
    walls: [],
    level: 1,
    timeElapsed: 0,
  });

  const CANVAS_WIDTH = 400;
  const CANVAS_HEIGHT = 400;

  const generateMaze = (levelNum) => {
    const walls = [];
    const cellSize = 20;
    const cols = Math.floor(CANVAS_WIDTH / cellSize);
    const rows = Math.floor(CANVAS_HEIGHT / cellSize);

    // Create outer walls
    for (let i = 0; i < cols; i++) {
      walls.push({ x: i * cellSize, y: 0, width: cellSize, height: cellSize });
      walls.push({
        x: i * cellSize,
        y: (rows - 1) * cellSize,
        width: cellSize,
        height: cellSize,
      });
    }
    for (let i = 0; i < rows; i++) {
      walls.push({ x: 0, y: i * cellSize, width: cellSize, height: cellSize });
      walls.push({
        x: (cols - 1) * cellSize,
        y: i * cellSize,
        width: cellSize,
        height: cellSize,
      });
    }

    // Generate internal maze based on level
    if (levelNum === 1) {
      const pattern1 = [
        [1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 0],
        [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        [1, 0, 1, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 1, 1, 0, 1, 0, 1, 0],
        [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        [1, 1, 1, 0, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0],
      ];
      for (let r = 0; r < pattern1.length; r++) {
        for (let c = 0; c < pattern1[r].length; c++) {
          if (pattern1[r][c] === 1) {
            walls.push({
              x: c * cellSize,
              y: (r + 2) * cellSize,
              width: cellSize,
              height: cellSize,
            });
          }
        }
      }
    } else if (levelNum === 2) {
      const pattern2 = [
        [1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0],
        [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        [1, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0],
        [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        [1, 0, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
        [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1, 0, 1, 0, 1, 0],
      ];
      for (let r = 0; r < pattern2.length; r++) {
        for (let c = 0; c < pattern2[r].length; c++) {
          if (pattern2[r][c] === 1) {
            walls.push({
              x: c * cellSize,
              y: (r + 1) * cellSize,
              width: cellSize,
              height: cellSize,
            });
          }
        }
      }
    } else {
      // Level 3+: more complex maze
      for (let r = 2; r < rows - 1; r++) {
        for (let c = 2; c < cols - 1; c++) {
          if (Math.random() < 0.25 && !(r === 1 && c === 1) && !(r === rows - 2 && c === cols - 2)) {
            walls.push({ x: c * cellSize, y: r * cellSize, width: cellSize, height: cellSize });
          }
        }
      }
    }

    return walls;
  };

  const startGame = () => {
    const walls = generateMaze(1);
    gameStateRef.current = {
      player: { x: 20, y: 20, width: 10, height: 10 },
      goal: { x: CANVAS_WIDTH - 40, y: CANVAS_HEIGHT - 40, width: 20, height: 20 },
      walls,
      level: 1,
      timeElapsed: 0,
    };
    setLevel(1);
    setTime(0);
    setScore(0);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const resetGame = () => {
    if (score > highScore) setHighScore(score);
    setGameState(GAME_CONFIG.states.MENU);
  };

  useEffect(() => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const game = gameStateRef.current;

    const keysPressed = {};

    const handleKeyDown = (e) => {
      keysPressed[e.key] = true;
    };

    const handleKeyUp = (e) => {
      keysPressed[e.key] = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    let timeCounter = 0;

    const gameLoop = setInterval(() => {
      // Clear canvas
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Update time
      timeCounter++;
      if (timeCounter % 30 === 0) {
        setTime((prev) => prev + 1);
      }

      // Player movement
      const moveSpeed = 2;
      if (keysPressed['ArrowLeft'] || keysPressed['a']) {
        game.player.x = Math.max(0, game.player.x - moveSpeed);
      }
      if (keysPressed['ArrowRight'] || keysPressed['d']) {
        game.player.x = Math.min(CANVAS_WIDTH - game.player.width, game.player.x + moveSpeed);
      }
      if (keysPressed['ArrowUp'] || keysPressed['w']) {
        game.player.y = Math.max(0, game.player.y - moveSpeed);
      }
      if (keysPressed['ArrowDown'] || keysPressed['s']) {
        game.player.y = Math.min(CANVAS_HEIGHT - game.player.height, game.player.y + moveSpeed);
      }

      // Wall collision detection
      game.walls.forEach((wall) => {
        if (
          game.player.x < wall.x + wall.width &&
          game.player.x + game.player.width > wall.x &&
          game.player.y < wall.y + wall.height &&
          game.player.y + game.player.height > wall.y
        ) {
          // Simple collision resolution - push player back
          const prevX = game.player.x - (keysPressed['ArrowRight'] || keysPressed['d'] ? moveSpeed : -moveSpeed);
          const prevY = game.player.y - (keysPressed['ArrowDown'] || keysPressed['s'] ? moveSpeed : -moveSpeed);
          game.player.x = prevX;
          game.player.y = prevY;
        }
      });

      // Goal collision detection
      if (
        game.player.x < game.goal.x + game.goal.width &&
        game.player.x + game.player.width > game.goal.x &&
        game.player.y < game.goal.y + game.goal.height &&
        game.player.y + game.player.height > game.goal.y
      ) {
        // Level complete
        const levelBonus = (3 - game.level) * 50 + (60 - time) * 10;
        setScore((prev) => prev + 500 + levelBonus);

        if (game.level < 3) {
          game.level++;
          const newWalls = generateMaze(game.level);
          game.walls = newWalls;
          game.player = { x: 20, y: 20, width: 10, height: 10 };
          game.goal = { x: CANVAS_WIDTH - 40, y: CANVAS_HEIGHT - 40, width: 20, height: 20 };
          setLevel(game.level);
          setTime(0);
        } else {
          setGameState(GAME_CONFIG.states.GAME_OVER);
          if (score > highScore) setHighScore(score);
        }
      }

      // Draw walls
      ctx.fillStyle = GAME_CONFIG.colors.primary;
      game.walls.forEach((wall) => {
        ctx.fillRect(wall.x, wall.y, wall.width, wall.height);
      });

      // Draw goal
      ctx.fillStyle = GAME_CONFIG.colors.success;
      ctx.beginPath();
      ctx.arc(
        game.goal.x + game.goal.width / 2,
        game.goal.y + game.goal.height / 2,
        game.goal.width / 2,
        0,
        Math.PI * 2
      );
      ctx.fill();

      // Draw player
      ctx.fillStyle = GAME_CONFIG.colors.accent;
      ctx.fillRect(game.player.x, game.player.y, game.player.width, game.player.height);

      // Draw HUD
      ctx.fillStyle = GAME_CONFIG.colors.light;
      ctx.font = '14px Arial';
      ctx.fillText(`Level: ${game.level}`, 10, 20);
      ctx.fillText(`Time: ${time}s`, 10, 40);
    }, 30);

    return () => {
      clearInterval(gameLoop);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameState, score, highScore, time]);

  return (
    <GameLayout
      title="Maze Solver 🌀"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {gameState === GAME_CONFIG.states.MENU && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Maze Solver</h2>
            <p style={styles.menuDescription}>
              Navigate through mazes to reach the goal! Use Arrow Keys or WASD to move.
            </p>
            <button
              onClick={startGame}
              className="btn btn-primary btn-lg"
              style={{ marginTop: '1rem' }}
            >
              🎮 Start Game
            </button>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && (
          <>
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              style={styles.canvas}
            />
            <div style={styles.stats}>
              <div>Level: {level}</div>
              <div>Time: {time}s</div>
            </div>
            <div style={styles.controls}>Arrow Keys/WASD: Move | Goal: Green Circle</div>
          </>
        )}

        {gameState === GAME_CONFIG.states.GAME_OVER && (
          <div style={styles.gameOverCard}>
            <h1 style={styles.gameOverTitle}>All Levels Complete!</h1>
            <ScoreDisplay score={score} stats={{ 'Final Score': score }} />
            <button
              onClick={startGame}
              className="btn btn-primary mt-3"
              style={{ width: '100%' }}
            >
              🔄 Play Again
            </button>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
  },
  canvas: {
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: '4px',
    backgroundColor: '#000',
    display: 'block',
  },
  stats: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    display: 'flex',
    gap: '2rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '0.5rem 1rem',
  },
  controls: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
  },
  gameOverCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    textAlign: 'center',
    maxWidth: '400px',
  },
  gameOverTitle: {
    color: GAME_CONFIG.colors.accent2,
    marginBottom: '1rem',
    fontSize: '2rem',
  },
};

export default MazeSolver;
